import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

const giftSelector = '[data-dm-desk-caster-gift]';
const pendingForms = new WeakSet();

function giftOfferEnd(gift) {
  const timestamp = Date.parse(gift.dataset.dmCasterOfferEndsAt || '');
  return Number.isFinite(timestamp) ? timestamp : null;
}

function giftOfferIsActive(gift, now = Date.now()) {
  const endsAt = giftOfferEnd(gift);
  return endsAt === null || now < endsAt;
}

function expireGiftOffer(gift) {
  gift.classList.add('is-expired');
  gift.querySelectorAll('[data-dm-caster-offer-content]').forEach((element) => element.remove());
  gift.hidden = true;
}

function getGiftForForm(form) {
  const section = form.closest('.shopify-section');
  if (!section) return null;

  const gift = section.querySelector(giftSelector);
  const productForm = form.closest('product-form-component');
  if (!gift || !productForm) return null;
  if (gift.dataset.productId !== productForm.dataset.productId) return null;

  const offerActive = giftOfferIsActive(gift);
  if (!offerActive) expireGiftOffer(gift);

  return { gift, offerActive, productForm, section };
}

function setFormDisabled(productForm, disabled) {
  productForm.querySelectorAll('[ref="addToCartButton"]').forEach((button) => {
    button.disabled = disabled;
  });
}

function showStatus(section, message) {
  const status = section.querySelector('[data-dm-caster-gift-status]');
  if (status) status.textContent = message;
}

function showFormError(productForm, message) {
  const error = productForm.querySelector('[ref="addToCartTextError"]');
  if (!error) return;

  error.classList.remove('hidden');
  const textNode = [...error.childNodes].find((node) => node.nodeType === Node.TEXT_NODE);
  if (textNode) textNode.textContent = message;
  else error.appendChild(document.createTextNode(message));
}

function selectedAddOns(section) {
  return [...section.querySelectorAll('[data-addon-variant]:checked')].map((checkbox) => ({
    checkbox,
    id: Number(checkbox.dataset.addonVariant),
    quantity: 1,
  }));
}

function cartSections() {
  return [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((component) => component.dataset.sectionId)
    .filter(Boolean);
}

async function readCart() {
  const response = await fetch('/cart.js', {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error('Could not read the cart. Please try again.');
  return response.json();
}

async function addDeskWithGift(event, form, context) {
  const { gift, offerActive, productForm, section } = context;
  const deskItems = event.detail.items.map((item) => ({ ...item }));
  const casterVariantId = Number(gift.dataset.casterVariantId);

  if (!deskItems.length || !casterVariantId
    || deskItems.some(({ id, quantity }) => !Number.isFinite(id) || id <= 0
      || !Number.isInteger(quantity) || quantity <= 0)) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  if (pendingForms.has(productForm)) return;
  pendingForms.add(productForm);

  // Preserve any line-item properties and selling plan on the native form.
  const formData = event.detail.formData;
  if (formData && deskItems.length === 1) {
    const properties = {};
    for (const [key, value] of formData) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && typeof value === 'string') properties[match[1]] = value;
    }
    if (Object.keys(properties).length) deskItems[0].properties = properties;
    if (formData.get('selling_plan')) deskItems[0].selling_plan = formData.get('selling_plan');
  }
  setFormDisabled(productForm, true);
  showStatus(section, offerActive ? 'Adding your desk and complimentary casters…' : 'Adding your desk…');

  const addOns = selectedAddOns(section);
  const deferred = CartLinesUpdateEvent.createPromise();
  const eventLines = deskItems.map(({ id, quantity }) => ({ merchandiseId: String(id), quantity }));
  const cartEvent = new CartLinesUpdateEvent({
    action: 'add',
    context: 'product',
    lines: eventLines,
    promise: deferred.promise,
  });
  productForm.dispatchEvent(cartEvent);

  try {
    const currentCart = await readCart();
    const casterAlreadyInCart = offerActive
      && currentCart.items.some((item) => Number(item.variant_id) === casterVariantId);
    const items = deskItems;

    if (offerActive && !casterAlreadyInCart) items.push({ id: casterVariantId, quantity: 1 });
    addOns.forEach(({ id, quantity }) => items.push({ id, quantity }));

    const sectionIds = cartSections();
    const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const result = await response.json();
    if (!response.ok || result.status) {
      throw new Error(result.description || result.message || 'Could not add these items.');
    }

    const cart = await readCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        didError: false,
        items: cart.items,
        itemCount: items.reduce((total, item) => total + item.quantity, 0),
        productId: productForm.dataset.productId,
        sections: result.sections,
        source: 'dm-desk-caster-gift',
        sourceId: productForm.id || '',
      },
    });

    addOns.forEach(({ checkbox }) => { checkbox.checked = false; });
    if (!offerActive) {
      showStatus(section, 'Desk added to cart.');
    } else {
      showStatus(
        section,
        casterAlreadyInCart
          ? 'Desk added. Your complimentary caster set is already in the cart.'
          : 'Desk and complimentary casters added to cart.'
      );
    }
  } catch (error) {
    const message = error?.message || 'Could not add these items. Please try again.';
    deferred.reject(error);
    productForm.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
    showFormError(productForm, message);
    showStatus(section, message);
  } finally {
    pendingForms.delete(productForm);
    setFormDisabled(productForm, false);
  }
}

document.addEventListener('product-form:before-add', (event) => {
  const productForm = event.target;
  if (!(productForm instanceof HTMLElement) || !productForm.matches('product-form-component')) return;
  const form = productForm.querySelector('form[action*="/cart/add"]');
  if (!form || !Array.isArray(event.detail?.items)) return;

  const context = getGiftForForm(form);
  if (!context) return;

  addDeskWithGift(event, form, context);
});
